import { CommonModule } from '@angular/common';
import { TrimInputDirective } from 'src/app/directives/trim.directive';
import { Component, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { CardComponent } from 'src/app/shared/components/master-detail-layout/card/card.component';
import { atLeastOneTrue, NewPrinterFormService } from '../../services/new-printer-form.service';
import { DocumentType, NewPrinterFormGroups } from '../../models/settings-printers.model';
import { CONNECTION_TYPES, DOCUMENTS_FIELDS, ENABLED_FIELDS, FISCAL_PRINTER, FISCAL_PROVIDERS, ORDER_PRINTER_DEFAULT_CONFIG, PRINTER_TYPES, Vendor, VENDORS } from '../../constants/new-printer.constants';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MainBaseComponent } from 'src/app/shared/components/master-detail-layout/main-base/main-base.component';
import { ActionBtnComponent } from 'src/app/shared/components/master-detail-layout/action-btn/action-btn.component';
import { TruncatedTextDirective } from 'src/app/directives/truncated-text.directive';
import { mobileCheck } from 'src/utilities';
import { PrintersModuleService } from '../../services/printers-module.service';
import { AlertDialogService, ConfirmDialogService } from 'src/app/dialogs';
import { $state } from 'app/ajs-upgraded-providers';
import { EntityManagerService, LoaderService } from 'src/app/core';
import { Printers } from 'tilby-models';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { PrinterNavigationStateService } from '../../services/printer-navigation-state.service';
import { disableAllExcept, enableAllExcept } from '../../utils/printers.utils';
import { BreakpointObserver } from '@angular/cdk/layout';

@Component({
  selector: 'app-new-printer',
  standalone: true,
  imports: [
    CommonModule,
    TrimInputDirective,
    CardComponent, MainBaseComponent, ActionBtnComponent,
    MatFormFieldModule, ReactiveFormsModule,
    MatRadioModule, MatInputModule, MatSelectModule,
    MatSlideToggleModule,
    TruncatedTextDirective,
    TranslateModule,
],
  templateUrl: './new-printer.component.html',
  styleUrl: './new-printer.component.scss'
})
export class NewPrinterComponent implements OnInit, OnDestroy {

  protected readonly newPrinterFormService = inject(NewPrinterFormService);
  private readonly translateService = inject(TranslateService);
  private readonly printersModuleService = inject(PrintersModuleService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly loaderService = inject(LoaderService);
  private readonly state = inject($state);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly entityManagerService = inject(EntityManagerService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly printerNavigationStateService = inject(PrinterNavigationStateService);

  protected newPrinterGroupForm!: NewPrinterFormGroups;

  protected printerTypesList = PRINTER_TYPES;
  protected vendorsList: Vendor[] = [];
  protected connectionTypesList = CONNECTION_TYPES;
  protected fiscalProvidersList = FISCAL_PROVIDERS;

  protected isMobile = mobileCheck();
  protected titlePage = this.translateService.instant('PRINTERS.NEW_DIALOG.TITLE');
  protected availableDocs: DocumentType[] = [];

  protected showConnectionFields = false;
  protected connectionCardOnRigth = false;

  protected subTitleClass = "tw-text-sm tw-font-semibold tw-text-[var(--sys-secondary-text)] tw-tracking-wider";
  protected linearValueClass = "tw-font-medium tw-text-[var(--sys-text)]";
  protected selectedValueClass = "tw-font-bold tw-text-[var(--sys-text)]";

  private breakpointObserver = inject(BreakpointObserver);
  
  isSmallDisplay = signal(false);

  constructor() {
    this.printersModuleService.updateSelection('NEW');
    this.newPrinterFormService.createForm();
    effect(() => {
      if(this.newPrinterFormService.printerFormReady()) {
        this.newPrinterGroupForm = this.newPrinterFormService.form;
      }
    })

    this.breakpointObserver.observe([
      '(max-width: 767px) or (orientation: portrait)'
    ])
    .pipe(takeUntilDestroyed())
    .subscribe(result => {
      this.isSmallDisplay.set(result.matches);
    });
  }

  ngOnInit() {
    this.printersModuleService.scrollToStart();
  }

  isFiscalOrRT() {
    return this.newPrinterFormService.type?.value === 'fiscal' || this.newPrinterFormService.type?.value === 'rt';
  }

  onChangeManifacturer() {
    const manifacturer = this.newPrinterFormService.manifacturer?.value as keyof typeof FISCAL_PRINTER; 
    var printerInfo = FISCAL_PRINTER[manifacturer];

    const type = this.newPrinterFormService.type?.value; 
    this.titlePage = type === 'rt' ? this.translateService.instant('PRINTERS.NEW.RT'): this.translateService.instant('PRINTERS.NEW.FP');

    enableAllExcept(this.newPrinterGroupForm, ENABLED_FIELDS);

    if(printerInfo) {
      this.newPrinterFormService.form.patchValue({
        driver: printerInfo.driver,
        connection_type: printerInfo.connectionType
      })
      
      this.titlePage += ' ' + printerInfo.label;
      this.showConnectionFields = true;
    } else {
      this.state.go('app.new.settings.printers.general');
    }
  }

  onChangePrinterType($event: MatRadioChange) {
    this.newPrinterFormService.manifacturer?.setValue(null, {emitEvent: false});
    this.resetFormValue();

    const type = $event.value; 
    this.connectionCardOnRigth = !!(type === 'nonfiscal' || type === 'kds' || type === 'receipt');
    this.printersModuleService.getAvailableDocumentTypes(type)
    this.availableDocs = this.printersModuleService.printerDocumentTypes;
    if(this.availableDocs?.length) {
      this.newPrinterGroupForm.setValidators([atLeastOneTrue(DOCUMENTS_FIELDS)])
    } else {
      this.newPrinterGroupForm.clearValidators();
    }

    switch (type) {
      case 'fiscal': case 'rt':
        this.titlePage = type === 'rt' ? this.translateService.instant('PRINTERS.NEW.RT'): this.translateService.instant('PRINTERS.NEW.FP');
        this.vendorsList = VENDORS[type as keyof typeof VENDORS] || [];
        
        disableAllExcept(this.newPrinterGroupForm, ENABLED_FIELDS);

        if(type === 'fiscal') {
          this.newPrinterFormService.manifacturer?.setValue('rch');
          this.onChangeManifacturer();
        }
        return;
      case 'nonfiscal':
        this.newPrinterFormService.form.patchValue({
          configuration: JSON.stringify(ORDER_PRINTER_DEFAULT_CONFIG),
          driver: 'escpos'
        })

        this.titlePage = this.translateService.instant('PRINTERS.NEW.ORDER');
        break;

      case 'receipt':
        this.newPrinterFormService.form.patchValue({
          driver: 'escpos'
        })

        this.titlePage = this.translateService.instant('PRINTERS.NEW.NONFISCAL');
        break;

      case 'kds':
        this.newPrinterFormService.form.patchValue({
          driver: 'http'
        });

        this.titlePage = this.translateService.instant('PRINTERS.NEW.KDS');
        break;
      default:
        this.state.go('app.new.settings.printers.general');
    }

    this.showConnectionFields = true;
    enableAllExcept(this.newPrinterGroupForm, ENABLED_FIELDS);
  }

  resetFormValue() {
    this.newPrinterFormService.form.patchValue({
      configuration: null,
      fiscal_provider: null,
      connection_type: null,
      print_e_invoice: false,
      print_fiscal_receipt: false,
      print_generic_document: false,
      print_generic_invoice: false,
      print_generic_receipt: false,
      print_invoice: false,
      print_receipt: false,
      print_receipt_invoice: false,
      print_shipping_invoice: false,
      print_summary_e_nrc: false,
      print_summary_e_rc: false,
      print_summary_invoice: false
    })
  }

  async goBack() {
    const confirmed = await this.printerNavigationStateService.checkPendingData();
    if (!confirmed) return;

    this.printersModuleService.updateSelection(undefined);
    this.printersModuleService.updateShowDetailMobile(false);
    this.state.go('app.new.settings.printers', { checkPendingData: false });
  }

  async cancel() {
    const proceed = await this.confirmDialogService.openDialog({
      data: { 
        messageLabel: this.translateService.instant('PRINTERS.NEW.WANT_TO_CANCEL')
      }
    });
    if (!proceed) return;

    if(!this.isMobile) {
      this.state.go('app.new.settings.printers.general', { checkPendingData: false });
    } else {
      this.printersModuleService.updateSelection(undefined);
      this.printersModuleService.updateShowDetailMobile(false);
      this.state.go('app.new.settings.printers', { checkPendingData: false });
    }
  }

  async save() {
    const proceed = await this.confirmDialogService.openDialog({
      data: { 
        messageLabel: this.translateService.instant('PRINTERS.NEW.WANT_TO_SAVE')
      }
    });
    if (!proceed) return;

    this.loaderService.show();
    try {
      // the manufacturer only drives the form: it is not a printer field
      const { manifacturer, ...printerToSave } = this.newPrinterFormService.form.getRawValue();

      const targetKeys: (keyof Printers)[] = ['ip_address', 'subnet_mask', 'gateway'];

      for (const key of targetKeys) {
        const value = printerToSave[key];
        
        if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
          printerToSave[key] = null;
        }
      }

      const result  = await this.entityManagerService.printers.postOneOnline(printerToSave as Printers);
      await this.printersModuleService.loadPrinterList();
      this.state.go('app.new.settings.printers.detail', { id: result.id, checkPendingData: false } );

      this._snackBar.open(this.translateService.instant('PRINTERS.DETAIL.SAVE_CREATE_OK'),
        undefined, 
        { 
          duration: 3000, 
          horizontalPosition: 'start', 
          verticalPosition: "bottom", 
        }
      );
    } catch (error: any) {
      const messageError = 
      error?.data?.error?.code === 71 ? 'PRINTERS.DETAIL.SAVE_ERROR_IP_ADDRESS' :
        'PRINTERS.DETAIL.SAVE_CREATE_GENERIC_ERROR';
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant(messageError)
        }
      });
    } finally {
      this.loaderService.hide();
    }
  }

  ngOnDestroy() {
  }
}
